import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";
import { useState } from "react";
import { api } from "../../../lib/api";
import { cn } from "../../../lib/utils";

type ExposurePhase = "shadow" | "live_readonly" | "history_enabled" | "quote_enabled";

type ConformanceScope = {
  acquisitionScopeId: string;
  apiFamily: "derivatives" | "gas_spot" | "index";
  commodityCode: string;
  areaCode: string;
  dataset: string;
  selectorType: string;
  selectorCode: string;
  phase: ExposurePhase;
  gateBoundToRun: boolean;
  healthHold: boolean;
  healthHoldReasonCodes: string[];
  entitlementState: "unknown" | "available" | "degraded" | "revoked";
  observationCount: number;
  observedTradingDays: number;
  latestObservedAt: string | null;
  latest: null | {
    feedState: string;
    scheduledPolls: number;
    completedPolls: number;
    failedPolls: number;
    queueLagP95Seconds: number | null;
    quotedSubjectCount: number;
    freshQuotedSubjectCount: number;
    quarantinedRowCount: number;
  };
  thresholdReady: boolean;
  reasonCodes: string[];
};

type ConformanceRun = {
  runId: string;
  state: "running" | "passed" | "failed" | "aborted";
  startedAt: string;
  endedAt: string | null;
  minimumTradingDays: number;
  artifactRecorded: boolean;
  thresholdReady: boolean;
  reasonCodes: string[];
  scopes: ConformanceScope[];
};

type RunsResponse = { asOf: string; runs: ConformanceRun[] };

export function ConformanceRolloutPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const runsQ = useQuery({
    queryKey: ["eex-market-data", "conformance-runs"],
    queryFn: () => api<RunsResponse>("/eex/market-data/conformance/runs?limit=20"),
    retry: false,
    refetchInterval: (query) => query.state.data?.runs.some((run) => run.state === "running")
      ? 30_000
      : false,
  });

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["eex-market-data", "conformance-runs"] }),
      queryClient.invalidateQueries({ queryKey: ["eex-market-data", "scope-onboarding-targets"] }),
      queryClient.invalidateQueries({ queryKey: ["eex-live-market"] }),
      queryClient.invalidateQueries({ queryKey: ["eex-quote-market"] }),
    ]);
  };

  const completeM = useMutation({
    mutationFn: ({ runId, outcome }: { runId: string; outcome: "passed" | "failed" | "aborted" }) =>
      api(`/eex/market-data/conformance/runs/${runId}/complete`, {
        method: "POST",
        body: JSON.stringify({ outcome }),
      }),
    onSuccess: async (_value, variables) => {
      setError(null);
      setFeedback(`Conformance run marked ${variables.outcome}.`);
      await invalidate();
    },
    onError: showError(setError),
  });

  const advanceM = useMutation({
    mutationFn: ({ scopeId, toPhase }: { scopeId: string; toPhase: Exclude<ExposurePhase, "shadow"> }) =>
      api(`/eex/market-data/conformance/scopes/${scopeId}/advance`, {
        method: "POST",
        body: JSON.stringify({ toPhase }),
      }),
    onSuccess: async (_value, variables) => {
      setError(null);
      setFeedback(`Scope advanced to ${phaseLabel(variables.toPhase)}.`);
      await invalidate();
    },
    onError: showError(setError),
  });

  const rollbackM = useMutation({
    mutationFn: (scopeId: string) =>
      api(`/eex/market-data/conformance/scopes/${scopeId}/rollback`, {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      setError(null);
      setFeedback("Scope rolled back to shadow. Cached evidence and manual workflows are unchanged.");
      await invalidate();
    },
    onError: showError(setError),
  });

  const busy = completeM.isPending || advanceM.isPending || rollbackM.isPending;

  return (
    <section className="max-w-6xl space-y-4 rounded-lg border border-border p-4">
      <div>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Activity size={16} /> Shadow conformance and rollout
        </div>
        <p className="mt-1 max-w-3xl text-xs text-muted">
          Every exact commodity, area, dataset and selector remains hidden in shadow until its real-data evidence
          passes. Exposure advances one phase at a time; rollback returns the scope directly to shadow without
          deleting market evidence or affecting manual quotes and trades. After activation, a failed health
          threshold immediately hides the scope and requires an administrator to roll it back to shadow.
        </p>
      </div>

      {runsQ.isLoading ? (
        <div className="text-xs text-muted">Loading conformance runs…</div>
      ) : runsQ.isError ? (
        <div className="text-xs text-negRed">Unable to load conformance status: {(runsQ.error as Error).message}</div>
      ) : runsQ.data?.runs.length === 0 ? (
        <div className="rounded border border-border p-3 text-xs text-muted">
          No shadow run exists yet. Select discovered scopes above and start them in shadow first.
        </div>
      ) : (
        <div className="space-y-4">
          {runsQ.data?.runs.map((run) => (
            <RunCard
              key={run.runId}
              run={run}
              busy={busy}
              onComplete={(outcome) => completeM.mutate({ runId: run.runId, outcome })}
              onAdvance={(scopeId, toPhase) => advanceM.mutate({ scopeId, toPhase })}
              onRollback={(scopeId) => rollbackM.mutate(scopeId)}
            />
          ))}
        </div>
      )}

      {feedback && <div className="text-xs text-posGreen">{feedback}</div>}
      {error && <div className="text-xs text-negRed">{error}</div>}
    </section>
  );
}

function RunCard({
  run,
  busy,
  onComplete,
  onAdvance,
  onRollback,
}: {
  run: ConformanceRun;
  busy: boolean;
  onComplete: (outcome: "passed" | "failed" | "aborted") => void;
  onAdvance: (scopeId: string, phase: Exclude<ExposurePhase, "shadow">) => void;
  onRollback: (scopeId: string) => void;
}) {
  return (
    <article className="space-y-3 rounded border border-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold">
            Run {shortId(run.runId)}
            <StateBadge state={run.state} ready={run.thresholdReady} />
          </div>
          <div className="mt-1 text-[11px] text-muted">
            Started {new Date(run.startedAt).toLocaleString()} · minimum {run.minimumTradingDays} trading days ·
            artifact {run.artifactRecorded ? "verified" : "pending"}
          </div>
        </div>
        {run.state === "running" && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || !run.thresholdReady}
              onClick={() => {
                if (confirm("Mark this conformance run passed? PostgreSQL will reject the action unless every required evidence gate is complete.")) {
                  onComplete("passed");
                }
              }}
              className="inline-flex items-center gap-1 rounded border border-posGreen/40 px-2 py-1 text-[11px] text-posGreen disabled:opacity-35"
            >
              <CheckCircle2 size={12} /> Pass run
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => confirm("Mark this conformance run failed?") && onComplete("failed")}
              className="rounded border border-border px-2 py-1 text-[11px] disabled:opacity-35"
            >
              Fail
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => confirm("Abort this conformance run?") && onComplete("aborted")}
              className="rounded border border-border px-2 py-1 text-[11px] disabled:opacity-35"
            >
              Abort
            </button>
          </div>
        )}
      </div>

      {run.reasonCodes.length > 0 && (
        <div className="flex items-start gap-2 rounded border border-amber/30 bg-amber/5 p-2 text-[11px] text-amber">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>{run.reasonCodes.map(reasonLabel).join(" · ")}</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-[11px]">
          <thead className="text-muted">
            <tr className="border-b border-border">
              <th className="py-1 pr-3 font-medium">Scope</th>
              <th className="py-1 pr-3 font-medium">Dataset</th>
              <th className="py-1 pr-3 font-medium">Evidence</th>
              <th className="py-1 pr-3 font-medium">Feed</th>
              <th className="py-1 pr-3 font-medium">Exposure</th>
              <th className="py-1 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {run.scopes.map((scope) => {
              const next = run.state === "passed" && scope.gateBoundToRun && !scope.healthHold
                ? nextPhase(scope.phase)
                : null;
              return (
                <tr key={scope.acquisitionScopeId} className="border-b border-border/60 align-top last:border-0">
                  <td className="py-2 pr-3">
                    <div className="font-medium">{scope.commodityCode} · {scope.areaCode}</div>
                    <div className="text-muted">{scope.apiFamily}{scope.selectorCode ? ` · ${scope.selectorCode}` : ""}</div>
                  </td>
                  <td className="py-2 pr-3">{scope.dataset.replaceAll("_", " ")}</td>
                  <td className="py-2 pr-3">
                    <div>{scope.observedTradingDays}/{run.minimumTradingDays} trading days</div>
                    <div className="text-muted">{scope.observationCount} samples</div>
                    {!scope.thresholdReady && scope.reasonCodes.length > 0 && (
                      <div className="mt-1 max-w-[260px] text-amber">{scope.reasonCodes.map(reasonLabel).join(" · ")}</div>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <div>{scope.latest?.feedState ?? "not sampled"}</div>
                    <div className="text-muted">entitlement {scope.entitlementState}</div>
                    {scope.healthHold && (
                      <div className="mt-1 max-w-[260px] text-negRed">
                        Health hold: {scope.healthHoldReasonCodes.map(reasonLabel).join(" · ")}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3"><PhaseBadge phase={scope.phase} healthHold={scope.healthHold} /></td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1.5">
                      {next && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => confirm(`Advance only this exact scope to ${phaseLabel(next)}?`) && onAdvance(scope.acquisitionScopeId, next)}
                          className="rounded border border-border px-2 py-1 disabled:opacity-35"
                        >
                          Enable {phaseLabel(next)}
                        </button>
                      )}
                      {scope.phase !== "shadow" && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => confirm("Roll this exact scope back to shadow now?") && onRollback(scope.acquisitionScopeId)}
                          className="inline-flex items-center gap-1 rounded border border-negRed/40 px-2 py-1 text-negRed disabled:opacity-35"
                        >
                          <RotateCcw size={11} /> Shadow
                        </button>
                      )}
                      {run.state === "passed" && !scope.gateBoundToRun && scope.phase === "shadow" && (
                        <span className="text-muted">Not the gate-bound run</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function StateBadge({ state, ready }: { state: ConformanceRun["state"]; ready: boolean }) {
  return (
    <span className={cn(
      "rounded border px-1.5 py-0.5 font-normal",
      state === "passed"
        ? "border-posGreen/40 text-posGreen"
        : state === "running" && ready
          ? "border-accent/40 text-accent"
          : state === "failed"
            ? "border-negRed/40 text-negRed"
            : "border-border text-muted"
    )}>
      {state === "running" && ready ? "ready for operator pass" : state}
    </span>
  );
}

function PhaseBadge({ phase, healthHold }: { phase: ExposurePhase; healthHold: boolean }) {
  return (
    <span className={cn(
      "rounded border px-1.5 py-0.5",
      healthHold
        ? "border-negRed/40 text-negRed"
        : phase === "shadow"
          ? "border-border text-muted"
          : "border-posGreen/40 text-posGreen"
    )}>
      {healthHold ? `${phaseLabel(phase)} · held` : phaseLabel(phase)}
    </span>
  );
}

function nextPhase(phase: ExposurePhase): Exclude<ExposurePhase, "shadow"> | null {
  if (phase === "shadow") return "live_readonly";
  if (phase === "live_readonly") return "history_enabled";
  if (phase === "history_enabled") return "quote_enabled";
  return null;
}

function phaseLabel(value: ExposurePhase): string {
  if (value === "live_readonly") return "live read-only";
  if (value === "history_enabled") return "history";
  if (value === "quote_enabled") return "Quote Builder";
  return "shadow";
}

function reasonLabel(value: string): string {
  return value.replaceAll("_", " ");
}

function shortId(value: string): string {
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function showError(setter: (value: string | null) => void) {
  return (value: unknown) => setter(value instanceof Error ? value.message : String(value));
}
